/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  TextInput,
  StatusBar,
  Alert,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

const CategoryCoursesScreen = ({ navigation, route }) => {
  const { category, categoryName } = route.params;
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [filterLevel, setFilterLevel] = useState('All');

  // Sample category data
  const categoryData = {
    Civil: {
      name: 'Civil Engineering',
      icon: 'bridge',
      color: '#0e4e96',
      description: 'Master the fundamentals of civil engineering, from structural design to construction management.',
    },
    Mechanical: {
      name: 'Mechanical Engineering',
      icon: 'cog',
      color: '#19abb5',
      description: 'Explore mechanical systems, thermodynamics, and advanced manufacturing techniques.',
    },
    Electrical: {
      name: 'Electrical Engineering',
      icon: 'flash',
      color: '#fdb913',
      description: 'Learn electrical systems, circuit analysis, and power engineering fundamentals.',
    },
    Computer: {
      name: 'Computer Engineering',
      icon: 'laptop',
      color: '#9C27B0',
      description: 'Dive into software development, algorithms, and computer systems design.',
    },
    Chemical: {
      name: 'Chemical Engineering',
      icon: 'flask',
      color: '#FF5722',
      description: 'Study chemical processes, reaction engineering, and industrial applications.',
    },
    Safety: {
      name: 'Safety Engineering',
      icon: 'shield-check',
      color: '#4CAF50',
      description: 'Focus on workplace safety, risk assessment, and regulatory compliance.',
    },
  };

  const currentCategory = categoryData[category] || categoryData.Civil;

  // Sample courses for the category
  // ── courses from /api/data/courses (replaces mock array) ──
  // Filters to the active category passed via route.params.
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const AsyncStorage = require('@react-native-async-storage/async-storage').default;
        const token = (await AsyncStorage.getItem('auth_token'))
                  || (await AsyncStorage.getItem('userToken'))
                  || (await AsyncStorage.getItem('token')) || '';
        const qs = category ? `?category=${encodeURIComponent(category)}&limit=100` : '?limit=100';
        const res = await fetch(`https://api.jobeng.ai/api/data/courses${qs}`, {
          headers: {
            'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const json = await res.json();
        const list = Array.isArray(json) ? json : (json.data || json.results || []);
        if (mounted) setCourses(list);
      } catch (e) { console.warn('[CategoryCoursesScreen] fetch courses failed:', e.message); }
      finally { if (mounted) setCoursesLoading(false); }
    })();
    return () => { mounted = false; };
  }, [category]);

  const levels = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  const handleSearch = () => {
    if (searchQuery.trim()) {
      Alert.alert('Search', `Searching for "${searchQuery}" in ${currentCategory.name}...`);
    }
  };

  const handleSort = () => {
    const sortOptions = [
      { label: 'Most Popular', value: 'popular' },
      { label: 'Highest Rated', value: 'rating' },
      { label: 'Price (Low to High)', value: 'price_asc' },
      { label: 'Price (High to Low)', value: 'price_desc' },
      { label: 'Newest First', value: 'newest' },
    ];

    Alert.alert(
      'Sort Courses',
      'Choose sorting option:',
      [
        ...sortOptions.map(option => ({
          text: option.label,
          onPress: () => setSortBy(option.value)
        })),
        { text: 'Cancel', style: 'cancel' }
      ]
    );
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <FontAwesome5 key={i} name="star" size={12} color="#fdb913" solid />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <FontAwesome5 key="half" name="star-half-alt" size={12} color="#fdb913" solid />
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <FontAwesome5 key={`empty-${i}`} name="star" size={12} color="#e0e0e0" />
      );
    }

    return stars;
  };

  const renderCourseCard = ({ item }) => (
    <TouchableOpacity
      style={styles.courseCard}
      onPress={() => navigation.navigate('CourseDetails', { courseId: item.id, course: item })}
    >
      {item.isFeatured && (
        <View style={styles.featuredBadge}>
          <Text style={styles.featuredText}>Featured</Text>
        </View>
      )}
      
      <View style={[styles.courseIconContainer, { backgroundColor: item.color }]}>
        <MaterialCommunityIcons name={item.icon} size={40} color="#ffffff" />
      </View>
      
      <View style={styles.courseInfo}>
        <Text style={styles.courseTitle} numberOfLines={2}>
          {item.title}
        </Text>
        <Text style={styles.courseInstructor} numberOfLines={1}>
          by {item.instructor}
        </Text>
        <Text style={styles.courseDescription} numberOfLines={2}>
          {item.description}
        </Text>
        
        <View style={styles.ratingContainer}>
          <View style={styles.starsContainer}>
            {renderStars(item.rating)}
          </View>
          <Text style={styles.ratingText}>
            {item.rating} ({item.students.toLocaleString()})
          </Text>
        </View>
        
        <View style={styles.metaContainer}>
          <Text style={styles.levelText}>{item.level}</Text>
          <Text style={styles.durationText}>{item.duration}</Text>
        </View>
        
        <View style={styles.tagsContainer}>
          {item.tags.slice(0, 3).map((tag, index) => (
            <View key={index} style={styles.tag}>
              <Text style={styles.tagText}>{tag}</Text>
            </View>
          ))}
        </View>
        
        <View style={styles.priceContainer}>
          {item.originalPrice > item.price && (
            <Text style={styles.originalPrice}>£{item.originalPrice}</Text>
          )}
          <Text style={styles.currentPrice}>£{item.price}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderLevelFilter = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.levelItem,
        filterLevel === item && styles.activeLevelItem,
      ]}
      onPress={() => setFilterLevel(item)}
    >
      <Text
        style={[
          styles.levelText,
          filterLevel === item && styles.activeLevelText,
        ]}
      >
        {item}
      </Text>
    </TouchableOpacity>
  );

  // Filter courses based on search and level
  const filteredCourses = courses.filter(course => {
    const matchesLevel = filterLevel === 'All' || course.level === filterLevel;
    const matchesSearch = searchQuery === '' || 
      String(course.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(course.instructor ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(course.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesLevel && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'price_asc':
        return a.price - b.price;
      case 'price_desc':
        return b.price - a.price;
      case 'popular':
      default:
        return b.students - a.students;
    }
  });

  const renderHeader = () => (
    <View>
      {/* Category Header */}
      <View style={styles.categoryHeader}>
        <View style={[styles.categoryIcon, { backgroundColor: currentCategory.color }]}>
          <MaterialCommunityIcons name={currentCategory.icon} size={32} color="#ffffff" />
        </View>
        <View style={styles.categoryInfo}>
          <Text style={styles.categoryTitle}>{currentCategory.name}</Text>
          <Text style={styles.categoryDescription}>{currentCategory.description}</Text>
          <Text style={styles.courseCount}>{filteredCourses.length} courses available</Text>
        </View>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons
          name="search"
          size={20}
          color="#64748B"
          style={styles.searchIcon}
        />
        <TextInput
          style={styles.searchInput}
          placeholder={`Search ${currentCategory.name} courses...`}
          value={searchQuery}
          onChangeText={setSearchQuery}
          onSubmitEditing={handleSearch}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>

      {/* Level Filters */}
      <View style={styles.filtersContainer}>
        <FlatList
          data={levels}
          renderItem={renderLevelFilter}
          keyExtractor={item => item}
          horizontal
          showsHorizontalScrollIndicator={false}
          nestedScrollEnabled={true}
        />
      </View>

      {/* Results Header */}
      <View style={styles.resultsHeader}>
        <Text style={styles.resultsText}>
          {filteredCourses.length} courses
          {filterLevel !== 'All' && ` • ${filterLevel} level`}
          {searchQuery && ` • "${searchQuery}"`}
        </Text>
        <TouchableOpacity style={styles.sortButton} onPress={handleSort}>
          <Ionicons name="swap-vertical" size={16} color="#0e4e96" />
          <Text style={styles.sortText}>Sort</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f0f4f8" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{currentCategory.name}</Text>
        <TouchableOpacity style={styles.menuButton}>
          <Ionicons name="ellipsis-vertical" size={24} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      {/* Main Content */}
      <FlatList
        data={filteredCourses}
        renderItem={renderCourseCard}
        keyExtractor={item => item.id}
        ListHeaderComponent={renderHeader}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={() => (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="school-outline" size={64} color="#64748B" />
            <Text style={styles.emptyTitle}>No courses found</Text>
            <Text style={styles.emptyText}>
              Try adjusting your search or filter criteria
            </Text>
            <TouchableOpacity 
              style={styles.clearButton} 
              onPress={() => {
                setSearchQuery('');
                setFilterLevel('All');
              }}
            >
              <Text style={styles.clearButtonText}>Clear Filters</Text>
            </TouchableOpacity>
          </View>
        )}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  menuButton: {
    padding: 5,
  },
  contentContainer: {
    padding: 15,
  },
  categoryHeader: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  categoryIcon: {
    width: 60,
    height: 60,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  categoryInfo: {
    flex: 1,
  },
  categoryTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 8,
  },
  categoryDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 8,
  },
  courseCount: {
    fontSize: 12,
    color: '#19abb5',
    fontWeight: '500',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 10,
    paddingHorizontal: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  searchIcon: {
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 16,
    color: '#333',
  },
  filtersContainer: {
    marginBottom: 15,
  },
  levelItem: {
    backgroundColor: '#e9eef4',
    borderRadius: 20,
    paddingVertical: 8,
    paddingHorizontal: 15,
    marginRight: 10,
  },
  activeLevelItem: {
    backgroundColor: '#0e4e96',
  },
  levelText: {
    color: '#0e4e96',
    fontWeight: 'bold',
    fontSize: 14,
  },
  activeLevelText: {
    color: '#fff',
  },
  resultsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  resultsText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  sortText: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 4,
    fontWeight: '500',
  },
  courseCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
    overflow: 'hidden',
    position: 'relative',
  },
  featuredBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: '#fdb913',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    zIndex: 1,
  },
  featuredText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  courseIconContainer: {
    width: '100%',
    height: 120,
    justifyContent: 'center',
    alignItems: 'center',
  },
  courseInfo: {
    padding: 15,
  },
  courseTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 4,
  },
  courseInstructor: {
    fontSize: 13,
    color: '#64748B',
    marginBottom: 6,
  },
  courseDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 18,
    marginBottom: 8,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  starsContainer: {
    flexDirection: 'row',
    marginRight: 8,
  },
  ratingText: {
    fontSize: 12,
    color: '#64748B',
  },
  metaContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  durationText: {
    fontSize: 12,
    color: '#64748B',
  },
  tagsContainer: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  tag: {
    backgroundColor: '#f0f4f8',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
  },
  tagText: {
    fontSize: 10,
    color: '#0e4e96',
    fontWeight: '500',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  originalPrice: {
    fontSize: 14,
    color: '#94A3B8',
    textDecorationLine: 'line-through',
    marginRight: 8,
  },
  currentPrice: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
  },
  clearButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  clearButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CategoryCoursesScreen;

